import { Button, SxProps } from "@mui/material";
import * as React from "react";
import { ResourceDWR } from "../../dto/ResourceDWR";
import { HoldingsInformation } from "../../dto/HoldingsInformation";
import { useLanguage } from "../Language";
import { AccountHolder } from "../Account";
import { useSupportedFunctionality } from "../SupportedFunctionality";
import { useLocationConfiguration } from "../LocationConfiguration";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { ResourceView } from "../../dto/ResourceView";
import { useVisibleHoldings } from "../pages/resource/useVisibleHoldings";
import { useAvailability } from "../../contexts/AvailabilityContext";
import { useSingleBranch } from "./useSingleBranch";
import { usePlaceHold } from "./PlaceHoldProvider";

export interface RequestButtonProps {
  resource: ResourceDWR | ResourceView;
  sx?: SxProps;
}

// Possible improvement, split logic into SerialRequestButton, ReserveRequestButton, and RequestButton (normal default material)
export const RequestButton: React.FC<RequestButtonProps> = ({
  resource,
  sx,
}) => {
  const { placeHold } = usePlaceHold();
  const { translate } = useLanguage();
  const { supportedFunctionality } = useSupportedFunctionality();
  const visibleHoldings = useVisibleHoldings(resource);
  const { singleFillBranchId, singlePickupBranchId } = useSingleBranch();
  const { getAvailabilityForHoldings } = useAvailability();
  const { locationConfiguration } = useLocationConfiguration();
  const [isDisabled, setDisabled] = React.useState(true);
  const { pacSettings } = locationConfiguration || {};
  const { userActionSettings } = pacSettings || {};
  const {
    allowItemLevelHolds,
    allowTitleLevelHolds,
    allowOnShelfHolds,
    // restrictTitleLevelHolds,
  } = userActionSettings || {};
  const { supportsAvailabilityFunctionality } = supportedFunctionality || {};

  const showRequestButton = React.useMemo(() => {
    if (
      AccountHolder.isAdmin ||
      !supportsAvailabilityFunctionality ||
      (!allowItemLevelHolds && !allowTitleLevelHolds) ||
      (resource.serial && !allowItemLevelHolds)
    ) {
      return false;
    }

    return true;
  }, [
    resource.serial,
    AccountHolder.isAdmin,
    supportsAvailabilityFunctionality,
    allowItemLevelHolds,
    allowTitleLevelHolds,
  ]);

  async function shouldDisableRequestButton(
    resource: ResourceDWR | ResourceView,
    visibleHoldings: HoldingsInformation[],
    allowOnShelfHolds: boolean,
    singleFillBranchId: string,
    singlePickupBranchId: string
  ) {
    const holdings = !!singleFillBranchId
      ? visibleHoldings.filter((h) => h.branchIdentifier === singleFillBranchId)
      : visibleHoldings;

    if (holdings.length === 0) return true;

    const availability = await getAvailabilityForHoldings(
      resource.id,
      holdings
    );
    const circulating = availability.filter(
      ({ nonCirculating }) => !nonCirculating
    );

    if (circulating.length === 0) return true;
    if (allowOnShelfHolds) return false;
    if (!singlePickupBranchId) return false; // if multiple branches available, defer to the dialog

    const onShelfAvailability = await getAvailabilityForHoldings(
      resource.id,
      visibleHoldings.filter((h) => h.branchIdentifier === singlePickupBranchId)
    );

    if (resource.serial) {
      // all available?
      return onShelfAvailability
        .filter(({ nonCirculating }) => !nonCirculating)
        .every(({ available }) => available); // serial items can be different from one another
    }

    // any available?
    return onShelfAvailability
      .filter(({ nonCirculating }) => !nonCirculating)
      .some(({ available }) => available);
  }

  React.useEffect(() => {
    let isMounted = true;
    if (!showRequestButton) {
      setDisabled(true);
      return;
    }

    shouldDisableRequestButton(
      resource,
      visibleHoldings,
      allowOnShelfHolds,
      singleFillBranchId,
      singlePickupBranchId
    ).then((disable) => {
      if (isMounted) {
        setDisabled(disable);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [
    showRequestButton,
    resource.id,
    singleFillBranchId,
    singlePickupBranchId,
    allowOnShelfHolds,
  ]);

  const onClickPlaceRequest = React.useCallback(() => {
    return placeHold(resource);
  }, [placeHold, resource]);

  if (resource.rtype !== 1 || resource.downloadable) {
    throw new Error("Incorrect use of RequestButton");
  }

  return (
    <React.Fragment>
      {showRequestButton === true && (
        <React.Fragment>
          <Button
            startIcon={<ScheduleIcon />}
            sx={{ ...sx, whiteSpace: "nowrap" }}
            variant="contained"
            disabled={isDisabled}
            onClick={onClickPlaceRequest}
          >
            {translate("placeRequest")}
          </Button>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};
